import { identityApiUrl } from '@/core/config/env';
import { request } from '@/core/http/client';
import type { AuthUser } from '@/core/auth/AuthProvider';

/**
 * FLUXOS DE CONTA
 *
 * Uma decisão atravessa quase todos estes endpoints: **não confirmar se um
 * e-mail existe**. Recuperação de senha, reenvio de confirmação e cadastro
 * respondem igual para endereço cadastrado e não cadastrado.
 *
 * Sem isso, o formulário de "esqueci minha senha" vira um oráculo: o
 * atacante testa uma lista de e-mails e descobre quem tem conta no portal —
 * que é exatamente a lista que ele quer para o próximo passo.
 *
 * O custo é uma mensagem menos precisa para quem digitou errado. Vale.
 */

/**
 * Base do servico de identidade. Cobre cadastro, login, RENOVACAO DE SESSAO e
 * saida. Recuperacao de senha e confirmacao de e-mail ainda nao existem la e
 * seguem no mock — mandar para a API real o que ela nao serve daria 404 onde
 * hoje ha simulacao funcionando.
 */
const identidade = { baseUrl: identityApiUrl };

export type SessionResponse = {
  accessToken: string;
  expiresInSeconds: number;
  user: AuthUser;
};

export type RegisterPayload = Record<string, unknown>;

/**
 * Uma manifestação do titular, como ela foi apresentada na tela.
 *
 * Leva a VERSÃO do documento e o TEXTO que estava ao lado da caixa. "Aceitou"
 * sem dizer o quê não prova nada: quando o texto muda, quem aceitou o anterior
 * não aceitou o novo — e é essa distinção que exige um aceite novo.
 */
export type ConsentPayload = {
  purpose: string;
  granted: boolean;
  documentKey?: string | null;
  documentVersion?: string | null;
  textShown?: string | null;
};

export type RegisterResponse = {
  /** `pending_verification` quando a política exige confirmar o e-mail. */
  status: 'active' | 'pending_verification';
  email: string;
};

const digitos = (valor: unknown) => String(valor ?? '').replace(/\D/g, '');

export async function register(
  values: RegisterPayload,
  consents: ConsentPayload[] = [],
): Promise<RegisterResponse> {
  return request<RegisterResponse>('/auth/register', {
    method: 'POST',
    ...identidade,

    /*
      O serviço de identidade recebe os campos direto, e não o pacote de
      `values` do formulário dinâmico: a API não deve conhecer o formato de um
      formulário que o painel pode reconfigurar amanhã.

      O TELEFONE E O DOCUMENTO PASSARAM A VIAJAR — antes eram digitados e
      descartados aqui. O formulário prometia "é por aqui que você fala com
      quem se interessar" e o número nunca chegava ao cadastro do anunciante;
      todo anunciante nascia sem telefone. Com o contato agora começando dentro
      da plataforma, é exatamente esse número que recebe o interessado.
    */
    body: {
      email: String(values.email ?? ''),
      password: String(values.senha ?? ''),
      name: String(values.nome ?? ''),
      phone: digitos(values.whatsapp) || null,
      document: digitos(values.documento) || null,
      creci: String(values.creci ?? '').trim() || null,

      /*
        O ACEITE VAI JUNTO COM A CONTA, na mesma requisição.

        Ou as duas coisas existem, ou nenhuma — o servidor grava as duas na
        mesma transação. Mandar o consentimento numa chamada seguinte abriria a
        janela em que a conta existe e o registro não, e é justamente essa
        conta que ninguém consegue provar depois: ela parece igual às outras.
      */
      consents,
    },
  });
}

export async function login(email: string, password: string, remember: boolean): Promise<SessionResponse> {
  return request<SessionResponse>('/auth/login', {
    method: 'POST',
    ...identidade,
    body: { email, password, remember },
  });
}

/** Resposta sempre igual, exista a conta ou não. */
export async function requestPasswordReset(email: string): Promise<{ sent: true }> {
  return request('/auth/password/forgot', { method: 'POST', ...identidade, body: { email } });
}

export type ResetTokenState = 'valid' | 'expired' | 'invalid' | 'used';

/**
 * Confere o token ANTES de mostrar o formulário. Deixar a pessoa escolher uma
 * senha nova e só então dizer "o link expirou" é desperdício de esforço dela.
 */
export async function checkResetToken(token: string): Promise<{ state: ResetTokenState; email: string | null }> {
  return request('/auth/password/check', { ...identidade, query: { token } });
}

export async function resetPassword(token: string, password: string): Promise<{ ok: true }> {
  return request('/auth/password/reset', { method: 'POST', ...identidade, body: { token, password } });
}

export type VerificationState = 'verified' | 'already_verified' | 'expired' | 'invalid';

export async function verifyEmail(token: string): Promise<{ state: VerificationState; email: string | null }> {
  return request('/auth/email/verify', { method: 'POST', ...identidade, body: { token } });
}

export async function resendVerification(email: string): Promise<{ sent: true }> {
  return request('/auth/email/resend', { method: 'POST', ...identidade, body: { email } });
}

// -------------------------------------------------------------- convite

export type InvitationState = {
  state: ResetTokenState;
  email: string | null;
  name: string | null;
  roleName: string | null;
};

/**
 * Confere o convite antes de mostrar o formulario.
 *
 * Devolve tambem NOME e PERFIL quando o link vale, e nao so o e-mail: quem
 * recebe um convite inesperado precisa ler "voce foi convidado como Corretor"
 * antes de escolher uma senha. Um formulario de senha sem contexto e
 * indistinguivel de phishing — e ensinar a equipe a ignorar esse detalhe e
 * pior do que nao mandar convite nenhum.
 */
export async function checkInvitation(token: string): Promise<InvitationState> {
  return request("/auth/invitation/check", { ...identidade, query: { token } });
}

export async function acceptInvitation(token: string, password: string): Promise<InvitationState> {
  return request("/auth/invitation/accept", {
    method: "POST",
    ...identidade,
    body: { token, password },
  });
}

/**
 * Devolve a sessao a partir do cookie de renovacao.
 *
 * E o que faz o login sobreviver ao F5: o token de acesso vive quinze minutos e
 * fica so em memoria — guarda-lo em `localStorage` entregaria a sessao a
 * qualquer XSS. Sem esta chamada apontando para a identidade, todo
 * recarregamento deslogava.
 */
/**
 * Chamada de renovacao em voo, compartilhada por todos os chamadores.
 *
 * O cookie de renovacao e de USO UNICO: a identidade consome o token e emite
 * outro na mesma familia. Reaparecer um token ja consumido e, para o servidor,
 * indistinguivel de cookie roubado — e a resposta correta dele e derrubar a
 * familia inteira.
 *
 * Duas chamadas simultaneas com o MESMO cookie caem exatamente nessa
 * armadilha. E acontece sozinho: o `StrictMode` monta o provedor duas vezes em
 * desenvolvimento, duas abas restauram a sessao juntas, o temporizador de
 * renovacao esbarra num reload. O resultado e o usuario deslogado sem ter feito
 * nada — e, quando as duas chamadas empatam, dois tokens validos na familia.
 *
 * Compartilhar a promessa faz N chamadas virarem UMA. Nao e otimizacao: e o
 * que impede a segunda de parecer um ataque.
 */
let renovacaoEmVoo: Promise<SessionResponse> | null = null;

export async function restoreSession(): Promise<SessionResponse> {
  if (renovacaoEmVoo) return renovacaoEmVoo;

  renovacaoEmVoo = request<SessionResponse>('/auth/session', { method: 'POST', ...identidade });

  try {
    return await renovacaoEmVoo;
  } finally {
    // Solta a trava mesmo em falha: um 401 precisa permitir tentar de novo
    // depois de um login novo, e nao travar a aba ate recarregar.
    renovacaoEmVoo = null;
  }
}

export async function endSession(): Promise<void> {
  return request('/auth/logout', { method: 'POST', ...identidade });
}
